import * as React from 'react'

export function useIntersectionObserver<T extends Element>(
  options: IntersectionObserverInit = {},
): [React.RefCallback<T>, IntersectionObserverEntry | null] {
  const { threshold = 1, root = null, rootMargin = '0px' } = options
  const [entry, setEntry] = React.useState<IntersectionObserverEntry | null>(null)

  const previousObserver = React.useRef<IntersectionObserver | null>(null)

  const customRef: React.RefCallback<T> = React.useCallback(
    node => {
      if (previousObserver.current) {
        previousObserver.current.disconnect()
        previousObserver.current = null
      }

      if (node?.nodeType === Node.ELEMENT_NODE) {
        const observer = new IntersectionObserver(
          ([entry]) => {
            setEntry(entry)
          },
          { threshold, root, rootMargin },
        )

        observer.observe(node)
        previousObserver.current = observer
      }
    },
    [threshold, root, rootMargin],
  )

  return [customRef, entry]
}

/**
 * Observes every descendant matching `selector` under `rootRef`. The one-node
 * hook disconnects when its ref moves; this one is for lists that need every
 * match. Callback identity is ignored so a new `onIntersect` does not
 * reconnect. Hoist array `threshold` values to module scope.
 */
export function useIntersectionObserverTargets(
  rootRef: React.RefObject<Element | null>,
  selector: string,
  onIntersect: IntersectionObserverCallback,
  options?: {
    threshold?: IntersectionObserverInit['threshold']
    rootMargin?: string
    observeKey?: unknown
    enabled?: boolean
  },
) {
  const { threshold = 0, rootMargin = '0px', observeKey, enabled = true } = options ?? {}
  const onIntersectRef = React.useRef(onIntersect)
  onIntersectRef.current = onIntersect

  React.useEffect(() => {
    const root = rootRef.current
    if (!root || !enabled) {
      return
    }

    const observer = new IntersectionObserver(
      (entries, observed) => {
        onIntersectRef.current(entries, observed)
      },
      { root, threshold, rootMargin },
    )

    for (const node of root.querySelectorAll(selector)) {
      observer.observe(node)
    }

    return () => {
      observer.disconnect()
    }
  }, [rootRef, selector, threshold, rootMargin, observeKey, enabled])
}
